import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";

const PayPalInstallment = ({ planId, planName, monthlyAmount, totalAmount, months, currency, tier, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatCurrency = (amt) => {
    const symbols = { gbp: '£', usd: '$', eur: '€' };
    return `${symbols[currency.toLowerCase()] || currency}${amt.toFixed(2)}`;
  };

  const handleSubscribe = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API}/payments/create-installment-subscription`,
        { 
          plan_id: planId,
          tier: tier,
          months: months,
          monthly_amount: monthlyAmount
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.approve_url) {
        // Redirect to PayPal for approval
        window.location.href = response.data.approve_url;
      } else {
        setError("Failed to create subscription. Please try again.");
      }
    } catch (err) {
      console.error("Subscription error:", err);
      setError(err.response?.data?.detail || "Failed to create subscription");
      toast.error(err.response?.data?.detail || "Failed to create subscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{t('installment.title')}</h2>
            <p className="text-sm text-gray-500">{planName}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 text-xl"
          >
            ×
          </button>
        </div>

        {/* Payment Summary */}
        <div className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-gray-100">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">{t('installment.youPay')}</p>
            <p className="text-4xl font-bold text-emerald-600">{formatCurrency(monthlyAmount)}</p>
            <p className="text-emerald-600 text-sm mt-1">{t('installment.perMonthFor', { months: months })}</p>
            <div className="mt-3 pt-3 border-t border-emerald-200">
              <p className="text-emerald-700 text-sm">
                {t('installment.totalCommitment')}: <span className="font-semibold">{formatCurrency(totalAmount)}</span>
              </p>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="p-6">
          <h3 className="font-medium text-gray-900 mb-4">{t('installment.howItWorks')}</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
              <div>
                <p className="text-gray-700 font-medium">{t('installment.step1Title')}</p>
                <p className="text-gray-500 text-sm">{t('installment.step1Desc', { price: formatCurrency(monthlyAmount) })}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
              <div>
                <p className="text-gray-700 font-medium">{t('installment.step2Title')}</p>
                <p className="text-gray-500 text-sm">{t('installment.step2Desc', { months: months })}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
              <div>
                <p className="text-gray-700 font-medium">{t('installment.step3Title')}</p>
                <p className="text-gray-500 text-sm">{t('installment.step3Desc')}</p>
              </div>
            </div>
          </div>

          {/* Commitment notice */}
          <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
            <p className="text-amber-800 text-sm">
              <span className="font-medium">{t('installment.commitmentNotice')}</span>
              {' '}{t('installment.commitmentDesc', { months: months, total: formatCurrency(totalAmount) })}
            </p>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Subscribe Button */}
          <button
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full mt-6 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-full h-14 text-lg font-semibold shadow-lg disabled:opacity-50 transition-all"
            data-testid="start-installment-btn"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                {t('common.processing')}
              </span>
            ) : (
              t('installment.startPayments', { price: formatCurrency(monthlyAmount) })
            )}
          </button>

          {/* Security info */}
          <div className="mt-6 flex items-center justify-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <span className="text-sm">🔒</span>
              <span>{t('payment.securePayment')}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-sm">🛡️</span>
              <span>{t('payment.encryptedData')}</span>
            </div>
          </div>

          {/* PayPal branding */}
          <div className="mt-4 pt-4 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-500 mb-2">{t('installment.managedBy')}</p>
            <img 
              src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png" 
              alt="PayPal" 
              className="h-6 mx-auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayPalInstallment;
